import { AppResourcePage } from '../../pages/app-resource-base';
import { constructAsync } from '../../../../webmodule-common/other/async-constructor';
import {
  ConnectionState,
  GlobalNotificationManager
} from '../../../../webmodule-common/other/ui/icons/icon-notification-signal';
import { createConcurrencyCheck, ResourceLocker } from '../../common/resource-lock';
import { customElement } from 'lit/decorators.js';
import {
  FabricationJobContainer,
  FabricationJobContainerManager
} from '../../../fabrication-jobs/data/fabrication-job-container';
import {
  FabricationJobDataEntryView,
  FabricationJobView
} from '../../../fabrication-jobs/views/fabrication-job-detail-view';
import {
  fireQuickErrorToast,
  fireQuickInformationToast,
  fireQuickSuccessToast
} from '../../../../webmodule-common/other/toast-away';
import { getCurrentUser } from '../../../../webmodule-common/other/api/current-user';
import { goStaticURL } from '../../../../webmodule-common/other/ui/resource-resolver';
import { LangAppFabricationJobPage } from '../../../../localization/scripts/dealer-franchisee/fabrication-jobs/pages/app-fabrication-job-lang';
import { LangOptimizationPanel } from '../../../../localization/scripts/fabrication-jobs/views/optimization-panel-lang';
import { ModalViewBase } from '../../../../webmodule-common/other/ui/data-entry-screen-base';
import { NullPromise } from '../../../../webmodule-common/interop/types/misc';
import { OptimizationState, SSEFabricationJob, SSEOptimizationResult } from '../../../api/fabrication-job-api';
import { WMEventSource } from '../../../api/event-source';

export class FabricationJobLock extends ResourceLocker {
  constructor(id: string, lockRevokedEvent: () => Promise<void>, concurrencyCheckEvent?: () => Promise<boolean>) {
    super(id, 'fabricationjob', 'fabrication-job', lockRevokedEvent, concurrencyCheckEvent);
  }
}

@customElement('app-fabrication-job')
export class AppFabricationJobPage extends AppResourcePage {
  private containerManager?: FabricationJobContainerManager;
  private _connectionStateUnsubscribe: (() => void) | null = null;
  private _reconnectRefreshInFlight = false;

  async createModalEditor(id: string): NullPromise<ModalViewBase> {
    const container = new FabricationJobContainer(id, null, []);
    this.containerManager = new FabricationJobContainerManager(container);
    this.containerManager.forceLocked = () => !this.lock?.isLockOwner;
    // Why subscribe here? The lifecycle panel lives in the view but the heading badge is
    // rendered by the page, and _sseHandler deliberately skips the acting user's own
    // events. Without this, your own transition updates the panel but not the badge, and
    // Lock In leaves the panel on its Draft render because onEnter does not re-fire while
    // you stay on the tab.
    //
    // Sibling containers (quote-view, project-view, purchase-order-view) subscribe from
    // inside the view, which owns everything they refresh. Here the badge is page-owned and
    // the panels are view-owned, so one subscription has to span both - hence subscribing at
    // the page and delegating panel refresh to the view's own refreshAll().
    this.containerManager.afterSave.push(async () => {
      await (this.view as FabricationJobDataEntryView | null)?.refreshAll();
      this.requestUpdate();
    });
    await this.containerManager.needsFabricationJob();

    return await constructAsync(
      new FabricationJobView({
        containerManager: this.containerManager,
        onDelete: async () => {
          const success = await this.containerManager!.deleteJob();
          if (success) {
            goStaticURL('/fabrication-jobs');
          }
        }
      })
    );
  }

  async redirectPath(): NullPromise<string> {
    return '/fabrication-jobs';
  }

  meta() {
    return {
      title: () => {
        const job = this.containerManager?.fabricationJob;
        return job
          ? `Job #${job.jobNumber} ${job.title || LangAppFabricationJobPage.fabricationJobTitle()}`
          : LangAppFabricationJobPage.fabricationJobTitle();
      },
      description: 'Fabrication job detail'
    };
  }

  protected createLock(id: string): ResourceLocker | undefined {
    const lockRevokedEvent = async () => {
      await this.view?.abandonAndClose(true);
      goStaticURL('/fabrication-jobs');
    };

    const concurrencyCheck = createConcurrencyCheck('FABRICATION.FABRICATIONJOB', id, async () => {
      await this.containerManager?.needsFabricationJob();
      return this.containerManager?.fabricationJob?.recordVersion ?? '';
    });

    return new FabricationJobLock(id, lockRevokedEvent, concurrencyCheck);
  }

  // Re-fetch job + items + optimization and redraw everything. Shared between
  // the fabrication-job SSE handler (cross-user mutations) and the
  // reconnect handler (recover events missed during an Offline window).
  private async _reloadAndRedraw(): Promise<void> {
    await this.containerManager?.loadFromServer();
    await (this.view as FabricationJobDataEntryView | null)?.refreshAll();
    this.requestUpdate();
  }

  // SSE: Listen for changes to this fabrication job
  private _sseHandler = async (_data: unknown) => {
    const data = _data as SSEFabricationJob | undefined;
    if (!data) return;
    if (data.id !== this.resourceId) return;
    // Skip changes made by the current user - they already see their own updates
    const currentUserId = getCurrentUser()?.id;
    if (currentUserId && data.lastModifiedUserId === currentUserId) return;

    await this._reloadAndRedraw();
    fireQuickInformationToast(LangAppFabricationJobPage.updatedByAnotherUser());
  };

  // SSE: Listen for optimization-result state changes for this job. Comes from
  // the BackOffice RunOptimizationFunction, so we never skip by
  // last-modified-user - the user who clicked Optimize still wants the flip.
  private _sseOptimizationHandler = async (_data: unknown) => {
    const data = _data as SSEOptimizationResult | undefined;
    if (!data || data.id !== this.resourceId) return;

    // Why capture before the refresh? Lock-in re-publishes the optimization
    // result while it is still Completed (it flips the job to Planned, not the
    // result). Without the previous state we cannot tell a genuine run
    // completion from that re-publish, and would fire the "Optimization
    // complete" toast a second time on top of the "Locked in" toast.
    const previousState = this.containerManager?.optimizationResult?.state;

    await this.containerManager?.refreshOptimizationResult();
    const detailView = this.view as FabricationJobDataEntryView | null;
    if (detailView?.refreshOptimizationPanel) {
      detailView.refreshOptimizationPanel();
    }
    this.requestUpdate();

    if (data.state === OptimizationState.Completed && previousState !== OptimizationState.Completed) {
      fireQuickSuccessToast(LangOptimizationPanel.optimizationCompletedToast());
    } else if (data.state === OptimizationState.Failed) {
      const errMsg = this.containerManager?.optimizationResult?.errorMessage ?? '';
      fireQuickErrorToast(LangOptimizationPanel.optimizationFailedToast(errMsg));
    }
  };

  // Why? If the EventSource drops while the BackOffice fires Complete, the
  // event is lost and the panel sticks on InProgress until F5. The in-flight
  // guard collapses rapid reconnect flaps to a single refresh.
  private _onConnectionStateChange = async (
    newState: ConnectionState,
    previousState: ConnectionState
  ): Promise<void> => {
    const wasDisconnected =
      previousState === ConnectionState.Offline || previousState === ConnectionState.ServiceRestarting;
    if (newState !== ConnectionState.Online || !wasDisconnected) return;
    if (this._reconnectRefreshInFlight) return;
    this._reconnectRefreshInFlight = true;
    try {
      await this._reloadAndRedraw();
    } finally {
      this._reconnectRefreshInFlight = false;
    }
  };

  connectedCallback() {
    super.connectedCallback();
    WMEventSource.getInstance().addEventListener(WMEventSource.fabricationJob, this._sseHandler);
    WMEventSource.getInstance().addEventListener(WMEventSource.optimizationResult, this._sseOptimizationHandler);
    this._connectionStateUnsubscribe ??= GlobalNotificationManager.getInstance().onConnectionStateChange(
      this._onConnectionStateChange
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    WMEventSource.getInstance().removeEventListener(WMEventSource.fabricationJob, this._sseHandler);
    WMEventSource.getInstance().removeEventListener(WMEventSource.optimizationResult, this._sseOptimizationHandler);
    this._connectionStateUnsubscribe?.();
    this._connectionStateUnsubscribe = null;
  }
}
